<template>
  <div>
    <nav-bar />
    <v-container fluid>
      <v-row justify="center">
        <v-col cols="auto">
          <v-datetime-picker
            label="From"
            v-model="from"
            :loading="dateTimeLoading"
            :dateFormat="dateFormat"
            :timeFormat="timeFormat"
            :date-picker-props="dateProps"
            :time-picker-props="timeProps"
            :text-field-props="textFieldProps"
          >
            <template slot="dateIcon">
              <v-icon> mdi-calendar </v-icon>
            </template>
            <template slot="timeIcon">
              <v-icon>mdi-alarm</v-icon>
            </template>
          </v-datetime-picker>
        </v-col>
        <v-col cols="auto">
          <v-datetime-picker
            label="To"
            v-model="to"
            :loading="dateTimeLoading"
            :dateFormat="dateFormat"
            :timeFormat="timeFormat"
            :date-picker-props="dateProps"
            :time-picker-props="timeProps"
            :text-field-props="textFieldProps"
          >
            <template slot="dateIcon">
              <v-icon> mdi-calendar </v-icon>
            </template>
            <template slot="timeIcon">
              <v-icon>mdi-alarm</v-icon>
            </template>
          </v-datetime-picker>
        </v-col>
        <v-col cols="auto" align-self="center" class="text-center">
          <v-btn color="primary" @click="search">
            <v-icon left> mdi-magnify</v-icon>
            search
          </v-btn>
        </v-col>
        <v-col cols="auto" align-self="center" class="text-center">
          <v-btn color="primary" :loading="downloadLoading" @click="download">
            <v-icon left> mdi-cloud-download</v-icon>
            download
          </v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-title class="subtitle-2">
              Mqtt Data Table for {{ deviceId }}
              <v-spacer></v-spacer>
              <v-switch
                v-model="ecofrostViewMode"
                label="All Columns"
                v-if="deviceType === 'ecofrost'"
                hide-details
                class="pt-0 mt-0"
              ></v-switch>
            </v-card-title>
            <v-divider></v-divider>
            <v-data-table
              dense
              :headers="tableHeaders"
              :fixed-header="true"
              :items="items"
              :options.sync="options"
              :server-items-length="itemLength"
              :loading="tableLoading"
              :items-per-page="itemsPerPage"
              :footer-props="footerProps"
              height="600px"
            ></v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import {
  dateOnlyFormat,
  getFromDate,
  dateFormat24hr,
  getDiffInDays
} from '../plugins/date';
import ecotronTableColumns from '../mixins/ecotron-table-columns';
import ecofrostTableColumns from '../mixins/ecofrost-table-columns';
import { nodeRestApi } from '../plugins/api';
import NavBar from '../components/NavBar';

const formatDateForApi = date => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default {
  name: 'mqtt-data-table',
  components: { NavBar },
  mixins: [ecotronTableColumns, ecofrostTableColumns],
  data() {
    return {
      currentDeviceDetails: {},
      from: null,
      to: null,
      dateFormat: 'dd/MM/yy',
      timeFormat: 'HH:mm',
      dateProps: {
        color: 'primary'
      },
      timeProps: {
        format: '24hr',
        color: 'primary'
      },
      textFieldProps: {
        prependIcon: 'mdi-calendar'
      },
      items: [],
      itemLength: 0,
      options: {},
      footerProps: {
        itemsPerPageOptions: [50, 100, 150, 200]
      },
      itemsPerPage: 50,
      dateTimeLoading: false,
      tableLoading: false,
      downloadLoading: false,
      ecofrostViewMode: false
    };
  },
  computed: {
    deviceType() {
      if (this.$route.name.includes('ecotron')) {
        return 'ecotron';
      } else if (this.$route.name.includes('ecofrost')) {
        return 'ecofrost';
      } else {
        return null;
      }
    },
    deviceId() {
      return this.$route.params.topic;
    },
    tableHeaders() {
      if (this.deviceType === 'ecotron') {
        return this.ecotronHeaders;
      } else if (this.deviceType === 'ecofrost') {
        if (this.ecofrostViewMode) {
          return this.ecofrostHeaders;
        } else {
          return this.ecofrostHeaders.filter(element =>
            this.ecofrostRequiredHeaders.includes(element.value)
          );
        }
      } else {
        return null;
      }
    }
  },
  watch: {
    options: {
      async handler() {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options;

        let createdAt = -1;
        if (sortBy.length > 0 && sortDesc.length > 0) {
          if (sortBy[0] === 'createdAt' && sortDesc[0] === true) {
            createdAt = -1;
          } else if (sortBy[0] === 'createdAt' && sortDesc[0] === false) {
            createdAt = 1;
          }
        }
        if (this.to && this.from) {
          await this.getDataFromApi({
            to: this.to,
            from: this.from,
            deviceType: this.deviceType,
            deviceId: this.deviceId,
            limit: itemsPerPage,
            offset: (page - 1) * itemsPerPage,
            createdAt
          });
        }
      },
      deep: true
    }
  },
  methods: {
    async getDataFromApi({
      to,
      from,
      deviceType,
      deviceId,
      limit,
      offset,
      createdAt
    }) {
      try {
        await this.$store.commit('setError', false);
        this.tableLoading = true;
        const response = await nodeRestApi({
          method: 'POST',
          url: `${deviceType}/mqtt/${deviceId}`,
          data: {
            from:
              deviceType === 'ecotron'
                ? formatDateForApi(from)
                : new Date(from).toISOString().split('.')[0] + 'Z',

            to:
              deviceType === 'ecotron'
                ? formatDateForApi(to)
                : new Date(to).toISOString().split('.')[0] + 'Z',

            limit,
            offset,
            createdAt
          }
        });
        this.items = response.data.records.map(element => {
          return {
            ...element,
            createdAt: moment(element.createdAt).format('DD/MM/YYYY HH:mm:ss')
          };
        });
        this.itemLength = response.data.count;
      } catch (error) {
        if (error.isAxiosError) {
          this.$store.commit('setError', error.response.data.error.message);
        } else {
          this.$store.commit('setError', error);
        }
      } finally {
        this.tableLoading = false;
      }
    },
    async search() {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      let createdAt = -1;
      if (sortBy.length > 0 && sortDesc.length > 0) {
        if (sortBy[0] === 'createdAt' && sortDesc[0] === true) {
          createdAt = -1;
        } else if (sortBy[0] === 'createdAt' && sortDesc[0] === false) {
          createdAt = 1;
        }
      }
      await this.getDataFromApi({
        to: this.to,
        from: this.from,
        deviceType: this.deviceType,
        deviceId: this.deviceId,
        limit: itemsPerPage,
        offset: (page - 1) * itemsPerPage,
        createdAt
      });
    },
    async download() {
      await this.$store.commit('setError', false);
      try {
        if (this.deviceType === 'ecotron') {
          if (!getDiffInDays(this.from, this.to, 30)) {
            throw 'Download date range for Ecotron is 15 days';
          }
        } else if (this.deviceType === 'ecofrost') {
          if (!getDiffInDays(this.from, this.to, 15)) {
            throw 'Download date range for Ecofrost is 15 days';
          }
        }
        this.downloadLoading = true;
        const response = await nodeRestApi({
          method: 'POST',
          url: `${this.deviceType}/xlsx/${this.deviceId}`,
          data: {
            from:
              this.deviceType === 'ecotron'
                ? formatDateForApi(this.from)
                : new Date(this.from).toISOString().split('.')[0] + 'Z',

            to:
              this.deviceType === 'ecotron'
                ? formatDateForApi(this.to)
                : new Date(this.to).toISOString().split('.')[0] + 'Z'
          },
          timeout: 600000
        });
        let fileURL = response.data.url;
        let fileLink = document.createElement('a');
        fileLink.href = fileURL;
        fileLink.setAttribute('target', '_self');
        document.body.appendChild(fileLink);
        fileLink.click();
      } catch (error) {
        if (error.isAxiosError) {
          this.$store.commit('setError', error.response.data.error.message);
        } else {
          this.$store.commit('setError', error);
        }
      } finally {
        this.downloadLoading = false;
      }
    }
  },
  async created() {
    this.dateTimeLoading = true;

    if (this.deviceType === 'ecotron') {
      // dispacting action to gather latest data of ecotron
      await this.$store.dispatch('ecotron/fetchEcotronCurrent', this.deviceId);
      // collecting the data from store
      this.currentDeviceDetails = this.$store.state.ecotron.ecotronCurrent;
      let tempTo = this.currentDeviceDetails.updatedAt;
      this.to = new Date(new Date(tempTo).setHours(23, 59, 59));
      let tempFrom = getFromDate(tempTo);
      this.from = new Date(tempFrom);
    } else if (this.deviceType === 'ecofrost') {
      // dispacting action to gather latest data of ecofrost
      await this.$store.dispatch(
        'ecofrost/fetchEcofrostCurrent',
        this.deviceId
      );
      // collecting the data from store
      this.currentDeviceDetails = this.$store.state.ecofrost.ecofrostCurrent;
      let tempTo = this.currentDeviceDetails.updatedAt;
      this.to = new Date(new Date(tempTo).setHours(23, 59, 59));
      let tempFrom = getFromDate(tempTo);
      this.from = new Date(tempFrom);
    }

    await this.getDataFromApi({
      to: this.to,
      from: this.from,
      deviceType: this.deviceType,
      deviceId: this.deviceId,
      limit: this.options.itemsPerPage,
      offset: (this.options.page - 1) * this.options.itemsPerPage,
      createdAt: -1
    });

    this.dateTimeLoading = false;
  }
};
</script>
